/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import msk from "msk";
import React, { useRef } from "react";

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

interface SingleInputProps {
  error: boolean;
}

const SingleInput = styled.input<SingleInputProps>`
  width: 60px;
  height: 70px;
  padding: 8px;
  font-size: 24px;
  color: #2f2f2f;
  text-align: center;
  appearance: none;
  border-radius: 5px;
  border: 1px solid #cccccc;

  :focus {
    box-shadow: 0px 0px 6px #fb972e4d;
    border: 1px solid #fb972e;

    ${(props) =>
      props.error &&
      css`
        border: 1px solid #c41700;
        box-shadow: 0px 0px 6px #c417004d;
      `}
  }

  transition: box-shadow 0.3s, border 0.3s;

  ${(props) =>
    props.error &&
    css`
      border: 1px solid #c41700;
      box-shadow: 0px 0px 6px #c417004d;
    `}
`;

interface PasscodeInputProps {
  passcode: string[];
  handleChangePasscode: (passcode: string[]) => void;
  error: boolean;
  handleChangeError: (error: string) => void;
}

export default function PasscodeInput(props: PasscodeInputProps) {
  const { passcode, handleChangePasscode, error, handleChangeError } = props;

  const inputs = [
    useRef<HTMLInputElement | null>(null),
    useRef<HTMLInputElement | null>(null),
    useRef<HTMLInputElement | null>(null),
    useRef<HTMLInputElement | null>(null),
  ];

  function handlePasswordKeyPressed(
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) {
    const pass = [...passcode];
    if (index <= 3 && index >= -1) {
      switch (event.key) {
        case "Backspace":
          if (pass[index] === "") pass[index - 1] = "";
          else pass[index] = "";
          handleChangePasscode(pass);
          inputs[index - 1]?.current?.focus();
          return;
        case "ArrowLeft":
          inputs[index - 1]?.current?.focus();
          return;

        case "ArrowRight":
          inputs[index + 1]?.current?.focus();
          return;

        default:
          if (event.key.charCodeAt(0) >= 48 && event.key.charCodeAt(0) <= 57) {
            const maskedValue = msk.fit(event.key, "S");

            pass[index] = maskedValue;
            handleChangePasscode(pass);

            if (index < 3 && index >= -1) inputs[index + 1].current?.focus();
            handleChangeError("");
          }

          break;
      }
    }
  }

  return (
    <Container>
      {inputs.map((_, index) => (
        <SingleInput
          onKeyDown={(evt) => handlePasswordKeyPressed(evt, index)}
          key={`input_${index}`}
          onChange={() => {}}
          error={error}
          ref={inputs[index]}
          type="password"
          value={passcode[index]}
          pattern="[0-9]*"
          inputMode="numeric"
          data-lpignore="true"
          autoComplete="off"
        />
      ))}
    </Container>
  );
}
